/**
 * BetaEndContext — single source of truth for the post-beta-end experience.
 *
 * Responsibilities:
 *  - Poll /api/me/beta-status every 60s and expose the workspace's state
 *    (show_end_modal, discount_active, discount_expires_in_seconds, prices)
 *  - The first time we see show_end_modal=true we auto-POST /api/beta/mark-seen
 *    which stamps beta_ended_seen_at on the server, starting the 24h discount
 *    window. Successive renders just tick the local clock down.
 *  - Allow the modal to be dismissed without subscribing (workspace goes into
 *    a "paused" state — read-only, billing-only). Route guards read `isPaused`.
 *  - Refresh on demand after a successful checkout return so the modal +
 *    banner drop away immediately.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const BetaEndContext = createContext(null);

// Resolve the workspace_id the user is currently looking at — from the URL
// when they're inside an agency workspace (e.g. /workspace/:id/...), and
// from localStorage as a fallback (set by the workspace switcher in
// DashboardLayout). Returns null if neither is present, in which case
// the backend falls back to the caller's own active_workspace_id.
const useViewedWorkspaceId = () => {
  const { pathname } = useLocation();
  const [storageWs, setStorageWs] = useState(() => {
    try { return localStorage.getItem('currentWorkspaceId') || null; } catch { return null; }
  });
  // Keep storage value reactive to the workspace switcher
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'currentWorkspaceId') setStorageWs(e.newValue || null);
    };
    window.addEventListener('storage', onStorage);
    // Also poll once per second for same-tab updates (storage event doesn't
    // fire in the same tab). Cheap — just a string compare.
    const iv = setInterval(() => {
      try {
        const v = localStorage.getItem('currentWorkspaceId');
        setStorageWs((prev) => (prev !== v ? v : prev));
      } catch { /* ignore */ }
    }, 1000);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(iv); };
  }, []);

  // URL pattern: /workspace/:id/... — that's the authoritative source
  // for "which workspace am I viewing right now".
  const m = pathname.match(/^\/workspace\/([^/]+)/);
  return m?.[1] || storageWs || null;
};

export const BetaEndProvider = ({ children }) => {
  const { api, user } = useAuth();
  const viewedWorkspaceId = useViewedWorkspaceId();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [localExpiresIn, setLocalExpiresIn] = useState(0);
  const markedSeenRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return null;
    try {
      const params = viewedWorkspaceId ? { workspace_id: viewedWorkspaceId } : undefined;
      const res = await api.get('/api/me/beta-status', { params });
      setStatus(res.data);
      setLocalExpiresIn(res.data?.discount_expires_in_seconds || 0);
      return res.data;
    } catch {
      return null;
    }
  }, [api, user, viewedWorkspaceId]);

  // Initial fetch + 60s poll. Re-fires whenever the viewed workspace
  // changes so admins switching workspaces see the new workspace's
  // beta state immediately (no 60s wait).
  useEffect(() => {
    if (!user) return;
    refresh();
    const iv = setInterval(refresh, 60 * 1000);
    // Also refresh instantly when a payment / balance event fires (e.g.
    // returning from a successful lifetime checkout). This flips
    // is_lifetime → true on this context immediately, which (a) closes
    // the LifetimeOfferModal if open and (b) prevents the countdown
    // badge from re-rendering on the next render pass.
    const onBalanceUpdated = () => { refresh(); };
    window.addEventListener('voxinity:balance-updated', onBalanceUpdated);
    return () => {
      clearInterval(iv);
      window.removeEventListener('voxinity:balance-updated', onBalanceUpdated);
    };
  }, [user, refresh]);

  // First-acknowledgement stamp → starts the 24h window server-side.
  // SKIP in admin-preview mode so admins viewing another workspace
  // don't accidentally start that workspace's discount window.
  useEffect(() => {
    if (!status?.show_end_modal) return;
    if (status?.is_admin_preview) return;
    if (markedSeenRef.current) return;
    if (status.beta_ended_seen_at) { markedSeenRef.current = true; return; }
    markedSeenRef.current = true;
    (async () => {
      try { await api.post('/api/beta/mark-seen'); await refresh(); } catch { /* ignore */ }
    })();
  }, [status, api, refresh]);

  // Local 1-second countdown so the UI stays snappy between server polls
  useEffect(() => {
    if (!status?.discount_active) return;
    const tick = setInterval(() => {
      setLocalExpiresIn((v) => (v > 0 ? v - 1 : 0));
    }, 1000);
    return () => clearInterval(tick);
  }, [status?.discount_active]);

  const showEndFlow = !!status?.show_end_modal;
  const isPaused = showEndFlow; // Workspace is paused until they subscribe
  const showMandatoryModal = showEndFlow && !dismissed;

  return (
    <BetaEndContext.Provider value={{
      status,
      isPaused,
      showMandatoryModal,
      dismissed,
      setDismissed,
      discountExpiresIn: localExpiresIn,
      refresh,
    }}>
      {children}
    </BetaEndContext.Provider>
  );
};

export const useBetaEnd = () => useContext(BetaEndContext) || {};
